import styled from "styled-components";
import {
  borderStyle,
  borderWidth,
} from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import {
  spacing,
  semanticSpacing,
} from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import {
  KG_TABLE_EXPANDED_FIELDS_MAX_HEIGHT_PX,
  KG_TABLE_MORE_FOOTER_HEIGHT_PX,
  KG_TABLE_NODE_WIDTH_PX,
} from "../../kg/kgNodeDimensions";
import {
  kgNodeEllipsisTextStyles,
  kgNodeTextContainerStyles,
} from "../../kg/kgNodeText.styles";

const tableRowPadding = `${semanticSpacing.squish.sm.vertical} ${semanticSpacing.squish.sm.horizontal}`;
const hairlineBorder = `${borderWidth.hairline} ${borderStyle.solid}`;

export const TableCardRoot = styled.div`
  width: ${KG_TABLE_NODE_WIDTH_PX}px;
  max-width: ${KG_TABLE_NODE_WIDTH_PX}px;
  box-sizing: border-box;
`;

/** Figma table header — tableBlue tint + hairline divider */
export const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  /* border-box so width:100% + padding doesn't overflow the card and push
     the text ellipsis past the frame's overflow clip. */
  box-sizing: border-box;
  gap: ${spacing[2]};
  padding: ${tableRowPadding};
  ${kgNodeTextContainerStyles}
  background: ${rawColors.accent.tableBlueBg};
  border-bottom: ${hairlineBorder} ${rawColors.accent.tableBlue};
  color: ${rawColors.accent.tableBlue};
`;

export const TableName = styled.span`
  flex: 1;
  min-width: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
  ${kgNodeEllipsisTextStyles}
`;

export const FieldList = styled.div<{
  $expanded: boolean;
}>`
  display: flex;
  flex-direction: column;
  /* Expanded lists cap at N rows and scroll internally; scrollbar is hidden
     so the canvas card keeps a clean fixed silhouette. */
  max-height: ${({ $expanded }) =>
    $expanded ? `${KG_TABLE_EXPANDED_FIELDS_MAX_HEIGHT_PX}px` : "none"};
  overflow-y: ${({ $expanded }) => ($expanded ? "auto" : "hidden")};
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  /* Rows must keep their natural height inside the capped, scrollable
     FieldList — otherwise flex squeezes their padding instead of scrolling. */
  flex-shrink: 0;
  box-sizing: border-box;
  gap: ${spacing[2]};
  padding: ${tableRowPadding};
  ${kgNodeTextContainerStyles}
  border-bottom: ${hairlineBorder} ${rawColors.stroke[200]};
`;

export const FieldName = styled.span`
  flex: 1;
  min-width: 0;
  ${typographyMixin("overline", 3)}
  color: ${rawColors.text.mediumEmphasis};
  ${kgNodeEllipsisTextStyles}
`;

export const MoreFooter = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  padding: ${tableRowPadding};
  max-height: ${({ $visible }) =>
    $visible ? `${KG_TABLE_MORE_FOOTER_HEIGHT_PX}px` : "0"};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  overflow: hidden;
  transition: max-height 0.25s ease, opacity 0.2s ease;
  pointer-events: none;
`;

export const MoreText = styled.span`
  ${typographyMixin("overline", 4)}
  color: ${rawColors.text.lowEmphasis};
  text-align: center;
  white-space: nowrap;
`;
